import { format, parse } from "date-fns";

export const buildGpDetails = (practiceDetails) => {
  const gpAddress = `${practiceDetails?.at(0)?.address?.addressLine1 || ""} ${
    practiceDetails?.at(0)?.address?.addressLine2 || ""
  } ${practiceDetails?.at(0)?.address?.addressLine3 || ""} ${
    practiceDetails?.at(0)?.address?.county || ""
  } ${practiceDetails?.at(0)?.address?.country || ""}`.trim();

  return {
    gpAddress,
    gpPhone: `${practiceDetails?.at(0)?.mainPhone || ""}`,
    gpCity: `${practiceDetails?.at(0)?.address?.city || ""}`,
    gpPostalCode: `${practiceDetails?.at(0)?.address?.postCode || ""}`,
  };
};

export const assembleAllFormsData = ({
  generalInformationForm,
  patientDetailsForm,
  referralForm,
  prostateAdditionalInfoForm,
  lungsAdditionalInfoForm,
  isLungsReferral,
  isProstateReferral,
  patientId,
  selectedFacility,
}) => {
  const activeAdditionalInfo = isLungsReferral
    ? lungsAdditionalInfoForm.getValues()
    : isProstateReferral
    ? prostateAdditionalInfoForm.getValues()
    : {};

  return {
    ...generalInformationForm.getValues(),
    ...patientDetailsForm.getValues(),
    ...referralForm.getValues(),
    ...activeAdditionalInfo,
    otherRadInvestigations: isProstateReferral
      ? prostateAdditionalInfoForm.getValues().otherRadInvestigationsProstate
      : lungsAdditionalInfoForm.getValues().otherRadInvestigations,
    patientId: patientId,
    hospitalName: selectedFacility?.hospitalName || "",
    currentMedication:
      prostateAdditionalInfoForm.getValues().currentMedication ||
      lungsAdditionalInfoForm.getValues().currentMedication ||
      referralForm.getValues().currentMedication ||
      "",
    // An unanswered anticoagulant question stays unanswered. Aspirin, Plavix and Warfarin
    // used to fall back to "false", so a referral could go out saying the patient was not
    // on one when nobody had said so — the defect behind KIN-656. The backend already
    // reads anything that is neither "true" nor "false" as "Not specified" on the wire, so
    // an empty value reports the truth instead of guessing.
    patientOnAnticoagulants:
      prostateAdditionalInfoForm.getValues().patientOnAnticoagulants ||
      lungsAdditionalInfoForm.getValues().patientOnAnticoagulants ||
      referralForm.getValues().patientOnAnticoagulants ||
      "",
    patientOnAspirin:
      (isProstateReferral
        ? prostateAdditionalInfoForm.getValues().patientOnAspirin
        : lungsAdditionalInfoForm.getValues().patientOnAspirin) || "",
    patientOnPlavix:
      (isProstateReferral
        ? prostateAdditionalInfoForm.getValues().patientOnPlavix
        : lungsAdditionalInfoForm.getValues().patientOnPlavix) || "",
    patientOnWarfarin:
      (isProstateReferral
        ? prostateAdditionalInfoForm.getValues().patientOnWarfarin
        : lungsAdditionalInfoForm.getValues().patientOnWarfarin) || "",
    aspirinDetails:
      (isProstateReferral
        ? prostateAdditionalInfoForm.getValues().aspirinDetails
        : lungsAdditionalInfoForm.getValues().aspirinDetails) || "",
    plavixDetails:
      (isProstateReferral
        ? prostateAdditionalInfoForm.getValues().plavixDetails
        : lungsAdditionalInfoForm.getValues().plavixDetails) || "",
    warfarinDetails:
      (isProstateReferral
        ? prostateAdditionalInfoForm.getValues().warfarinDetails
        : lungsAdditionalInfoForm.getValues().warfarinDetails) || "",
  };
};

export const buildCommonPayload = ({
  allFormsData,
  selectedFacility,
  staffMember,
  isSpecialist,
  isLungsReferral,
  isProstateReferral,
  gpDetails,
}) => {
  const { gpAddress, gpPhone, gpCity, gpPostalCode } = gpDetails;

  return {
    fileAttachments: allFormsData.fileAttachment,
    outOfHoursNumber: allFormsData.gpOutofHoursNumber,
    isSpecialist: isSpecialist,
    staffMember: staffMember,
    mothersMaidenName: allFormsData.mothersMaidenName,
    clinicalExamination: allFormsData.clinicalExamination,
    preferredConsultantFamilyName: allFormsData.preferredConsultantFamilyName,
    preferredConsultantFirstName: allFormsData.preferredConsultantFirstName,
    email: allFormsData.email,
    phone: allFormsData.phone,
    mobile: allFormsData.mobile,
    streetAddress: allFormsData.streetAddress,
    addressLine2: allFormsData.addressLine2,
    addressLine3: allFormsData.addressLine3,
    addressLine4: allFormsData.addressLine4,
    eirCode: allFormsData.eircode,
    gender: allFormsData.gender,
    dateOfBirth: format(
      parse(allFormsData.dateOfBirth, "dd-MM-yyyy", new Date()),
      "yyyy-MM-dd"
    ),
    familyName: allFormsData.familyName,
    firstName: allFormsData.firstName,
    title: allFormsData.title,
    currentMedication: allFormsData.currentMedication,
    hospitalName: selectedFacility?.hospitalName,
    hospitalId: selectedFacility?.hospitalId,
    interpreterRequired: allFormsData.interpreterRequired,
    wheelchairAssistanceRequired: allFormsData.wheelchairAssistance,
    relevantFamilyHistory: allFormsData.relevantFamilyHistory,
    departmentAgencyCode: selectedFacility?.departmentAgencyCode,
    departmentAgencyName: selectedFacility?.departmentAgencyName,
    hospitalCheckDigit: `${selectedFacility?.hospitalId}.${selectedFacility?.departmentAgencyCode}`,
    messageTypeId: selectedFacility?.messageTypeId?.value,
    responseMessageTypeId:
      selectedFacility?.messageTypeId?.responseMessageTypeId,
    referralPriority: allFormsData.referralPriority,
    referralType: allFormsData.referralType,
    patientOnAnticoagulants: allFormsData.patientOnAnticoagulants,
    medications:
      isLungsReferral || isProstateReferral
        ? [
            ...(allFormsData.patientOnAnticoagulants === "true" &&
            allFormsData.patientOnAspirin === "true"
              ? [
                  {
                    name: "Aspirin",
                    dosage: allFormsData.aspirinDetails,
                  },
                ]
              : []),
            ...(allFormsData.patientOnAnticoagulants === "true" &&
            allFormsData.patientOnPlavix === "true"
              ? [
                  {
                    name: "Plavix",
                    dosage: allFormsData.plavixDetails,
                  },
                ]
              : []),
            ...(allFormsData.patientOnAnticoagulants === "true" &&
            allFormsData.patientOnWarfarin === "true"
              ? [
                  {
                    name: "Warfarin",
                    dosage: allFormsData.warfarinDetails,
                  },
                ]
              : []),
          ]
        : allFormsData.anticoagulantMedications?.filter(
            (medication) => !!medication.name
          ) || [],
    firstLanguage: allFormsData.firstLanguage,
    drinker: allFormsData.drinker,
    smoker: allFormsData.smoker,
    unitsOfAlcohol: allFormsData.unitsOfAlcohol,
    yearsSmoking:
      allFormsData.smoker &&
      allFormsData.smoker !== "NON_SMOKER" &&
      allFormsData.smoker !== "UNKNOWN"
        ? allFormsData.yearsSmoking
        : null,
    cigarettesSmokedPerDay:
      allFormsData.smoker &&
      allFormsData.smoker !== "NON_SMOKER" &&
      allFormsData.smoker !== "UNKNOWN"
        ? allFormsData.numberOfCigarettes
        : null,
    nextOfKin: allFormsData.nextOfKin,
    additionalInformation: allFormsData.additionalInformation,
    laboratoryInvestigation: allFormsData.laboratoryInvestigation,
    radiologyInvestigation: allFormsData.radiologyInvestigation,
    reasonForReferral: allFormsData.reasonForReferral,
    historyOfPresentingComplains: allFormsData.historyOfPresentingComplains,
    previousHospitalAttendance: allFormsData.previousHospitalAttendance,
    historyOfPastIllness: allFormsData.historyOfPastIllness,
    historyOfSurgicalProcedures: allFormsData.historyOfSurgicalProcedures,
    allergiesAdverseMedicationEvents:
      allFormsData.allergiesAdverseMedicationEvents,
    patientId: allFormsData.patientId,
    bmi: isLungsReferral || isProstateReferral ? "" : allFormsData.bmi,
    bodyHeight:
      isLungsReferral || isProstateReferral ? "" : allFormsData.bodyHeight,
    weight: isLungsReferral || isProstateReferral ? "" : allFormsData.weight,
    bp1: isLungsReferral || isProstateReferral ? "" : allFormsData.bp1,
    bp2: isLungsReferral || isProstateReferral ? "" : allFormsData.bp2,
    pulse: isLungsReferral || isProstateReferral ? "" : allFormsData.pulse,
    gpOutofHoursNumber: allFormsData.gpOutofHoursNumber,
    gpPhone,
    gpAddress,
    gpCity,
    gpPostalCode,
    ...(allFormsData.anticoagulantMedications?.filter(
      (medication) => !!medication.name
    ) || []),
  };
};

export const buildLungPayload = (commonPayload, allFormsData) => ({
  ...commonPayload,
  lungs: {
    chestSigns: allFormsData.chestSigns,
    haemoptysis: allFormsData.haemoptysis,
    clubbing: allFormsData.clubbing,
    lymphadenopathy: allFormsData.lymphadenopathy,
    hepatosplenomegaly: allFormsData.hepatosplenomegaly,

    historyOfAsthma: allFormsData.historyOfAsthma,
    historyOfRentalInsufficiency: allFormsData.historyOfRentalInsufficiency,
    historyOfAllergyToContrastDye: allFormsData.historyOfAllergyToContrastDye,
    hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer:
      allFormsData.hasPatientBeenAdvisedOfPossibleDiagnosisOfLungCancer,

    otherPersistentUnexplainedSymptoms:
      allFormsData.otherPersistentUnexplainedSymptoms,
    otherClinicalFindings: allFormsData.otherClinicalFindings,
    lungLabInvestigation: allFormsData.lungLabInvestigation,
    specifiedChestSigns: allFormsData.specifiedChestSigns,
    otherRadInvestigations: allFormsData.otherRadInvestigations,

    radInvestigation1:
      allFormsData.radInvestigation1 === "not-done"
        ? ""
        : allFormsData.radInvestigation1,
    radInvestigation1Hospital:
      allFormsData.radInvestigation1 === "not-done"
        ? ""
        : allFormsData.radInvestigation1Hospital,
    radInvestigation1Date:
      allFormsData.radInvestigation1 === "not-done"
        ? null
        : allFormsData.radInvestigation1Date
        ? format(
            parse(allFormsData.radInvestigation1Date, "dd-MM-yyyy", new Date()),
            "yyyy-MM-dd"
          )
        : null,
    radInvestigation1Result:
      allFormsData.radInvestigation1 === "not-done"
        ? null
        : allFormsData.radInvestigation1Result,
    radInvestigation1Comment:
      allFormsData.radInvestigation1 !== "not-done" &&
      allFormsData.radInvestigation1Result === "Abnormal"
        ? allFormsData.radInvestigation1Comment
        : "",

    radInvestigation2:
      allFormsData.radInvestigation2 === "not-done"
        ? ""
        : allFormsData.radInvestigation2,
    radInvestigation2Hospital:
      allFormsData.radInvestigation2 === "not-done"
        ? ""
        : allFormsData.radInvestigation2Hospital,
    radInvestigation2Date:
      allFormsData.radInvestigation2 === "not-done"
        ? null
        : allFormsData.radInvestigation2Date
        ? format(
            parse(allFormsData.radInvestigation2Date, "dd-MM-yyyy", new Date()),
            "yyyy-MM-dd"
          )
        : null,
    radInvestigation2Result:
      allFormsData.radInvestigation2 === "not-done"
        ? ""
        : allFormsData.radInvestigation2Result,
    radInvestigation2Comment:
      allFormsData.radInvestigation2 !== "not-done" &&
      allFormsData.radInvestigation2Result === "Abnormal"
        ? allFormsData.radInvestigation2Comment
        : "",
  },
});

export const buildProstatePayload = (commonPayload, allFormsData) => ({
  ...commonPayload,
  prostate: {
    psaTests:
      allFormsData.psaTests?.map((test) => ({
        psa: test.psa,
        date: test.date
          ? format(parse(test.date, "dd-MM-yyyy", new Date()), "yyyy-MM-dd")
          : "",
      })) || [],

    hasSeenUrologist: allFormsData.hasSeenUrologist,
    hasSeenUrologistConsultant:
      allFormsData.hasSeenUrologist === "yes"
        ? allFormsData.hasSeenUrologistConsultant
        : null,
    hasSeenUrologistLocations:
      allFormsData.hasSeenUrologist === "yes"
        ? allFormsData.hasSeenUrologistLocations
        : null,
    dateOfUrologyConsult:
      allFormsData.hasSeenUrologist === "yes" &&
      allFormsData.dateOfUrologyConsult
        ? format(
            parse(allFormsData.dateOfUrologyConsult, "dd-MM-yyyy", new Date()),
            "yyyy-MM-dd"
          )
        : "Not Seen Urologist",

    hasPreviousBiopsy: allFormsData.hasPreviousBiopsy,
    hasPreviousBiopsyHospital:
      allFormsData.hasPreviousBiopsy === "yes"
        ? allFormsData.hasPreviousBiopsyHospital
        : null,
    hasPreviousBiopsyDate:
      allFormsData.hasPreviousBiopsy === "yes" &&
      allFormsData.hasPreviousBiopsyDate
        ? format(
            parse(allFormsData.hasPreviousBiopsyDate, "dd-MM-yyyy", new Date()),
            "yyyy-MM-dd"
          )
        : "Had no previous Biopsy",
    hasPreviousBiopsyResult:
      allFormsData.hasPreviousBiopsy === "yes"
        ? allFormsData.hasPreviousBiopsyResult
        : null,

    digitalRectalExam: allFormsData.digitalRectalExam,
    otherClinicalExam: allFormsData.otherClinicalExam,
    resultOfUrinalysis: allFormsData.resultOfUrinalysis,
    otherLabInvestigations: allFormsData.otherLabInvestigations,
    otherRadInvestigations: allFormsData.otherRadInvestigations,

    currentMedication: allFormsData.currentMedication,
    patientOnAnticoagulants: allFormsData.patientOnAnticoagulants,
    patientOnAspirin:
      allFormsData.patientOnAnticoagulants === "true"
        ? allFormsData.patientOnAspirin
        : null,
    patientOnPlavix:
      allFormsData.patientOnAnticoagulants === "true"
        ? allFormsData.patientOnPlavix
        : null,
    patientOnWarfarin:
      allFormsData.patientOnAnticoagulants === "true"
        ? allFormsData.patientOnWarfarin
        : null,
    aspirinDetails:
      allFormsData.patientOnAnticoagulants === "true"
        ? allFormsData.aspirinDetails
        : null,
    plavixDetails:
      allFormsData.patientOnAnticoagulants === "true"
        ? allFormsData.plavixDetails
        : null,
    warfarinDetails:
      allFormsData.patientOnAnticoagulants === "true"
        ? allFormsData.warfarinDetails
        : null,
  },
});
